import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Divider,
  Grid,
  Checkbox,
  TextField,
  Paper,
  Stack,
  IconButton,
  Tooltip,
  Fade,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { JustDropDown } from "../utils/Form/Dropdown";
import BreakdownDialog from "../utils/dialog/breakdownDialog";
import ValidityPeriod from "../utils/components/yearPeriod";
import AddNewCa from "../utils/components/addNewCa";
import CaSettingsTable from "../utils/components/caSettingTable";
import { getCaSettings } from "../../builders/settings/CaSetting";
import { TextFieldForAmount } from "../utils/TextField/NumericTextField";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import usePreventPageUnload from "../../utils/hooks/usePreventPageUnload";
import { AuthContext } from "../../contexts/AuthContext";
import ComponentLoading from "../App/ComponentLoading";
import { useErrorDialog } from "../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../contexts/SuccessDialogContext";

// Fixed CA Schedule Type & Summary Combinations
const caScheduleOptions = [
  { type: "S19A", summary: "S19A(1)" },
  { type: "S19A", summary: "S19A(2 to 10A)" },
  { type: "S19", summary: "S19" },
  { type: "S19B", summary: "S19B" },
  { type: "S19D", summary: "S19D" },
  { type: "S16", summary: "S16 - LIA/IBA" },
];

const CaSettingPage = () => {
  const isFirstRender = useRef(true);
  const defaultSetting = { startPeriod: 2024, endPeriod: 2050 };
  const [caSettings, setCaSettings] = useState([]);
  const [newCaName, setNewCaName] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false);
  const [currentCa, setCurrentCa] = useState(null);
  const [loading, setLoading] = useState(true);
  const authContext = useContext(AuthContext);

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const {
    getCASettings,
    updateCaSettings,
    setData,
    resetData,
    global_dataState,
  } = authContext;

  usePreventPageUnload(global_dataState["caSettings"]?.unsaved);
  // Fetch CA settings on component mount
  useEffect(() => {
    const fetchCASettings = async () => {
      try {
        const response = await getCASettings();
        if (response && response.caSettings && response.caSettings.length > 0) {
          setCaSettings(response.caSettings);
        } else {
          console.warn("API returned empty CA settings. Using defaults.");
          setCaSettings(getCaSettings());
        }
      } catch (error) {
        console.error("Failed to fetch CA settings:", error);
        handleError("Error loading CA settings. Using default values.");
        setCaSettings(getCaSettings());
      } finally {
        setLoading(false);
        isFirstRender.current = true;
        resetData("caSettings");
      }
    };

    fetchCASettings();
  }, []);

  const DEBOUNCE_DELAY = 500; // Delay in ms
  const prevCaSettingsRef = useRef(caSettings);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false; // Mark as no longer the first render
        return;
      }

      const hasCaSettingChanged =
        JSON.stringify(prevCaSettingsRef.current) !==
        JSON.stringify(caSettings);

      if (hasCaSettingChanged) {
        setData("caSettings", "all", {
          caSettings: caSettings,
        });

        // Update the refs to the current state
        prevCaSettingsRef.current = caSettings;
      }
    }, DEBOUNCE_DELAY);

    // Cleanup function to clear timeout
    return () => clearTimeout(handler);
  }, [caSettings]);

  const handleAddCaSetting = () => {
    if (!newCaName.trim()) return;

    const newSetting = {
      id: uuidv4(), // Unique, stable ID
      key: newCaName.trim(),
      caType: "",
      caSummary: "",
      startPeriod: null,
      endPeriod: null,
      isFieldVariable: {
        numberOfYears: false,
        IA: false,
      },
      numberOfYears: null,
      allowanceType: { AA: true, IA: false },
      straightLineMethod: false,
      IA: null,
      AAbreakdown: [],
      baseAllowanceCap: false,
      cap: null,
      enhancedAllowance: false,
      enhancedAllowancePercentage: 0,
      furtherAllowanceCap: null,
      isEditable: true,
      isOnlyEnhanced: false,
    };

    setCaSettings([...caSettings, newSetting]);
    setNewCaName("");
    handleEdit(newSetting);
  };

  const handleEdit = (ca) => {
    //console.log("Editing CA:", ca);
    if (!ca.id) {
      ca.id = uuidv4();
    }
    setCurrentCa(ca);
    setEditDialogOpen(true);
  };

  const handleRemove = (caToRemove) => {
    setCaSettings((prevSettings) =>
      prevSettings.filter((item) => {
        if (item.id && caToRemove.id) {
          return item.id !== caToRemove.id;
        }

        return item.key !== caToRemove.key;
      })
    );
  };

  const handleClose = () => {
    setEditDialogOpen(false);
  };

  const handleConfirm = () => {
    setCaSettings(
      caSettings.map((item) => (item.id === currentCa.id ? currentCa : item))
    );
    setEditDialogOpen(false);
  };

  const handleSaveAllSettings = async () => {
    if (!caSettings || Object.keys(caSettings).length === 0) {
      console.warn("No CA settings provided to save.");
      return;
    }

    try {
      await updateCaSettings({ caSettings });
      handleSuccess("CA settings updated successfully!");
      resetData("caSettings");
    } catch (error) {
      handleError("Failed to update CA settings. Please try again later.");
    }
  };

  if (loading) {
    return <ComponentLoading />;
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 4 },
        display: "flex",
        flexDirection: "column",
        gap: 4,
        minHeight: "140vh",
        bgcolor: "background.default",
      }}
    >
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack spacing={2}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Stack direction='row' spacing={2} alignItems='center'>
              <SettingsOutlinedIcon color='primary' sx={{ fontSize: 32 }} />
              <Typography
                variant='h4'
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  fontSize: { xs: "1.5rem", sm: "2rem" },
                }}
              >
                CA Settings Management
              </Typography>
            </Stack>
          </Box>
          <Typography variant='body1' color='text.secondary'>
            Manage and configure Capital Allowance (CA) settings for your tax
            calculations.
          </Typography>
        </Stack>
      </Paper>

      {/* Add New CA Card */}
      <AddNewCa
        newCaName={newCaName}
        setNewCaName={setNewCaName}
        handleCreate={handleAddCaSetting}
      />

      {/* CA Settings Table and Save Button */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <CaSettingsTable
            caSettings={caSettings}
            handleEdit={(ca) => handleEdit(ca)}
            handleRemove={handleRemove}
            setCaSettings={setCaSettings}
          />
        </Paper>

        {/* Save All Settings Button */}
        <Paper
          elevation={2}
          sx={{
            position: "sticky",
            bottom: 16,
            zIndex: 10,
            p: 2,
            borderRadius: 3,
            bgcolor: "background.paper",
            boxShadow: "0px 4px 20px rgba(96, 63, 151, 0.15)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            {global_dataState["caSettings"]?.unsaved
              ? "You have unsaved changes"
              : "All changes saved"}
          </Typography>
          <Button
            variant='contained'
            color='primary'
            onClick={handleSaveAllSettings}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "#4F337A",
                boxShadow: "0px 8px 16px rgba(96, 63, 151, 0.2)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            Save All Settings
          </Button>
        </Paper>
      </Box>

      {/* Edit CA Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0px 8px 24px rgba(96, 63, 151, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction='row' spacing={2} alignItems='center'>
            <SettingsOutlinedIcon color='primary' />
            <Typography variant='h5' fontWeight='bold' color='text.primary'>
              Edit CA Setting
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          {currentCa && (
            <Stack spacing={4}>
              {/* CA Schedule Details Section */}
              <Paper
                variant='outlined'
                sx={{
                  p: 3,
                  borderRadius: 2,
                  borderColor: "divider",
                }}
              >
                <Stack spacing={3}>
                  <Box>
                    <Stack
                      direction='row'
                      spacing={1}
                      alignItems='center'
                      mb={2}
                    >
                      <Typography
                        variant='subtitle1'
                        fontWeight='bold'
                        color='text.primary'
                      >
                        CA Schedule Details
                      </Typography>
                      <Tooltip title='Configure the basic details of your Capital Allowance schedule'>
                        <IconButton size='small'>
                          <InfoOutlinedIcon fontSize='small' color='action' />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Divider />
                  </Box>

                  <TextField
                    fullWidth
                    label='CA Name'
                    value={currentCa.key}
                    onChange={(e) =>
                      setCurrentCa({ ...currentCa, key: e.target.value })
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <JustDropDown
                        label='CA Schedule Type'
                        value={currentCa.caType}
                        options={[
                          ...new Set(caScheduleOptions.map((opt) => opt.type)),
                        ]}
                        onChange={(e) =>
                          setCurrentCa({ ...currentCa, caType: e.target.value })
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <JustDropDown
                        label='CA Summary'
                        value={currentCa.caSummary}
                        options={caScheduleOptions
                          .filter((opt) => opt.type === currentCa.caType)
                          .map((opt) => opt.summary)}
                        onChange={(e) =>
                          setCurrentCa({
                            ...currentCa,
                            caSummary: e.target.value,
                          })
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>

              {/* Validity Period Section */}
              <Paper
                variant='outlined'
                sx={{
                  p: 3,
                  borderRadius: 2,
                  borderColor: "divider",
                }}
              >
                <ValidityPeriod
                  currentCa={currentCa}
                  setCurrentCa={setCurrentCa}
                  defaultSetting={defaultSetting}
                />
              </Paper>

              {/* Allowance Details Section */}
              <Paper
                variant='outlined'
                sx={{
                  p: 3,
                  borderRadius: 2,
                  borderColor: "divider",
                }}
              >
                <Stack spacing={3}>
                  <Box>
                    <Stack
                      direction='row'
                      spacing={1}
                      alignItems='center'
                      mb={2}
                    >
                      <Typography
                        variant='subtitle1'
                        fontWeight='bold'
                        color='text.primary'
                      >
                        Allowance Details
                      </Typography>
                      <Tooltip title='Configure allowance caps and enhanced allowances'>
                        <IconButton size='small'>
                          <InfoOutlinedIcon fontSize='small' color='action' />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Divider />
                  </Box>

                  {/* Base Allowance Cap */}
                  <Paper
                    variant='outlined'
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderColor: "divider",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "primary.lighter",
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      <Box
                        display='flex'
                        alignItems='center'
                        justifyContent='space-between'
                      >
                        <Typography variant='subtitle2' fontWeight='bold'>
                          Base Allowance Cap
                        </Typography>
                        <Checkbox
                          checked={currentCa.baseAllowanceCap}
                          onChange={() =>
                            setCurrentCa({
                              ...currentCa,
                              baseAllowanceCap: !currentCa.baseAllowanceCap,
                              cap: !currentCa.baseAllowanceCap
                                ? currentCa.cap
                                : null,
                            })
                          }
                          sx={{
                            color: "primary.main",
                            "&.Mui-checked": { color: "primary.main" },
                          }}
                        />
                      </Box>
                      {currentCa.baseAllowanceCap && (
                        <Fade in={currentCa.baseAllowanceCap}>
                          <Box>
                            <Typography
                              variant='body2'
                              color='text.secondary'
                              gutterBottom
                            >
                              Allowance Cap Amount
                            </Typography>
                            <TextFieldForAmount
                              value={currentCa.cap || ""}
                              onChange={(val) =>
                                setCurrentCa({ ...currentCa, cap: val })
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "primary.main",
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Fade>
                      )}
                    </Stack>
                  </Paper>

                  {/* Enhanced Allowance */}
                  <Paper
                    variant='outlined'
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderColor: "divider",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "primary.lighter",
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      <Box
                        display='flex'
                        alignItems='center'
                        justifyContent='space-between'
                      >
                        <Typography variant='subtitle2' fontWeight='bold'>
                          Enhanced Allowance
                        </Typography>
                        <Checkbox
                          checked={currentCa.enhancedAllowance}
                          onChange={() =>
                            setCurrentCa({
                              ...currentCa,
                              enhancedAllowance: !currentCa.enhancedAllowance,
                              enhancedAllowancePercentage:
                                !currentCa.enhancedAllowance
                                  ? currentCa.enhancedAllowancePercentage
                                  : 0,
                              furtherAllowanceCap: !currentCa.enhancedAllowance
                                ? currentCa.furtherAllowanceCap
                                : null,
                            })
                          }
                          sx={{
                            color: "primary.main",
                            "&.Mui-checked": { color: "primary.main" },
                          }}
                        />
                      </Box>
                      {currentCa.enhancedAllowance && (
                        <Fade in={currentCa.enhancedAllowance}>
                          <Stack spacing={2}>
                            <Box>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                                gutterBottom
                              >
                                Enhanced Allowance Percentage (%)
                              </Typography>
                              <TextField
                                type='number'
                                value={
                                  currentCa.enhancedAllowancePercentage || ""
                                }
                                onChange={(e) =>
                                  setCurrentCa({
                                    ...currentCa,
                                    enhancedAllowancePercentage: e.target.value,
                                  })
                                }
                                fullWidth
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "primary.main",
                                      },
                                  },
                                }}
                                inputProps={{ min: 0, max: 100 }}
                              />
                            </Box>
                            <Box>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                                gutterBottom
                              >
                                Further Allowance Cap
                              </Typography>
                              <TextFieldForAmount
                                value={currentCa.furtherAllowanceCap || ""}
                                onChange={(val) =>
                                  setCurrentCa({
                                    ...currentCa,
                                    furtherAllowanceCap: val,
                                  })
                                }
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "primary.main",
                                      },
                                  },
                                }}
                              />
                            </Box>
                          </Stack>
                        </Fade>
                      )}
                    </Stack>
                  </Paper>

                  {/* AA Breakdown Button */}
                  <Button
                    variant='outlined'
                    color='primary'
                    onClick={() => setBreakdownDialogOpen(true)}
                    startIcon={<SettingsOutlinedIcon />}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderStyle: "dashed",
                      borderWidth: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": {
                        borderStyle: "dashed",
                        bgcolor: "primary.lighter",
                      },
                    }}
                  >
                    Set Allowance Breakdown
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            bgcolor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={handleClose}
            variant='outlined'
            color='inherit'
            sx={{
              textTransform: "none",
              borderRadius: 2,
              mr: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant='contained'
            color='primary'
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              "&:hover": {
                boxShadow: "0px 4px 12px rgba(96, 63, 151, 0.2)",
              },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Breakdown Dialog */}
      {currentCa && (
        <BreakdownDialog
          open={breakdownDialogOpen}
          onClose={() => setBreakdownDialogOpen(false)}
          currentCa={currentCa}
          setCurrentCa={setCurrentCa}
        />
      )}
    </Box>
  );
};

export default CaSettingPage;
